import { useParams, Link } from "react-router-dom";
import { blogPosts } from "@/mocks/blogPosts";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnonymousThoughts from "./AnonymousThoughts";

export default function BlogPostDetail() {
  const { id } = useParams();
  const post = blogPosts.find((p) => p.id === id);

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#e6f6f5] mx-auto mb-4">
              <i className="ri-article-line text-[#4db8b1] text-2xl"></i>
            </div>
            <h1
              className="text-2xl font-light text-[#2d2d2d] mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Article Not Found
            </h1>
            <p className="text-[#6b6b6b] text-sm mb-6">
              The article you are looking for does not exist.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 bg-[#418b86] hover:bg-[#4db8b1] text-white text-sm font-medium px-6 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Blog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedPosts = blogPosts
    .filter((p) => p.id !== post.id && p.category === post.category)
    .slice(0, 2);

  if (relatedPosts.length < 2) {
    const others = blogPosts
      .filter((p) => p.id !== post.id && !relatedPosts.find((rp) => rp.id === p.id))
      .slice(0, 2 - relatedPosts.length);
    relatedPosts.push(...others);
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        {/* Hero Image */}
        <div className="relative h-[300px] md:h-[450px] overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/20"></div>
        </div>

        {/* Article Header */}
        <div className="bg-white py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-[#418b86] text-sm font-medium hover:text-[#4db8b1] transition-colors mb-6 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-sm"></i>
              Back to Blog
            </Link>

            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-1.5 bg-[#e6f6f5] px-3 py-1.5 rounded-full text-xs font-medium text-[#418b86]">
                <i className="ri-bookmark-line text-sm"></i>
                {post.category}
              </span>
            </div>

            <h1
              className="text-3xl md:text-5xl font-light text-[#2d2d2d] leading-snug mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {post.title}
            </h1>

            <div className="flex items-center gap-4 text-[#9a9a9a] text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-sm"></i>
                {post.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-sm"></i>
                {post.readTime}
              </span>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="bg-[#f8fcfc] py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <article className="bg-white rounded-2xl border border-[#e8f4f3] p-6 md:p-10">
              {post.content?.map((section, index) => (
                <div key={index} className={index > 0 ? "mt-8" : ""}>
                  {section.heading && (
                    <h2
                      className="text-xl md:text-2xl font-medium text-[#2d2d2d] mb-4"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {section.heading}
                    </h2>
                  )}
                  <div className="text-[#4a4a4a] text-sm md:text-base leading-relaxed space-y-4">
                    {section.body.map((paragraph, pIndex) => (
                      <p key={pIndex}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              ))}

              {/* Takeaway Box */}
              {post.takeaways && (
                <div className="mt-10 bg-[#e6f6f5] rounded-xl p-6 md:p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-lightbulb-line text-[#418b86] text-sm"></i>
                    <h3
                      className="text-lg font-medium text-[#2d2d2d]"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      Key Takeaways
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {post.takeaways.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#418b86] text-white text-xs shrink-0 mt-0.5">
                          {i + 1}
                        </span>
                        <span className="text-[#4a4a4a] text-sm leading-relaxed">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </article>
          </div>
        </div>

        {/* Related Articles */}
        {relatedPosts.length > 0 && (
          <div className="bg-white py-16 md:py-20">
            <div className="max-w-3xl mx-auto px-6 md:px-10">
              <p className="text-[#418b86] text-xs font-medium tracking-widest uppercase mb-3">
                Continue Reading
              </p>
              <h2
                className="text-2xl md:text-3xl font-light text-[#2d2d2d] leading-snug mb-10"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Related <span className="italic font-semibold">Articles.</span>
              </h2>

              <div className="space-y-6">
                {relatedPosts.map((rp) => (
                  <Link
                    key={rp.id}
                    to={`/blog/${rp.id}`}
                    className="flex items-start gap-5 bg-[#f8fcfc] rounded-xl border border-[#e8f4f3] p-5 hover:border-[#c8e8e6] transition-all duration-300 cursor-pointer group"
                  >
                    <div className="w-24 h-24 md:w-32 md:h-32 rounded-lg overflow-hidden shrink-0">
                      <img
                        src={rp.image}
                        alt={rp.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex-1 py-1">
                      <span className="text-[#418b86] text-xs font-medium mb-2 block">
                        {rp.category}
                      </span>
                      <h3
                        className="text-base md:text-lg font-medium text-[#2d2d2d] leading-snug mb-2 group-hover:text-[#418b86] transition-colors"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {rp.title}
                      </h3>
                      <span className="text-[#9a9a9a] text-xs flex items-center gap-1">
                        <i className="ri-time-line text-sm"></i>
                        {rp.readTime}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Anonymous Thoughts */}
        <AnonymousThoughts />
      </main>
      <Footer />
    </div>
  );
}